import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { Header, SectionLabel } from "../components/Brand";
import { api } from "../lib/api";
import { toast } from "sonner";

const BRANCHES = [
  "Computer Science & Engineering",
  "Information Technology",
  "Electronics & Communication",
  "Electrical Engineering",
  "Mechanical Engineering",
  "Civil Engineering",
  "Chemical Engineering",
  "Biotechnology",
  "Other",
];
const YEARS = ["1st Year", "2nd Year", "3rd Year", "4th Year", "Final Year", "Graduate"];

export default function StartFormPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    branch: "",
    year: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    if (!form.full_name.trim()) return "Full name is required";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return "Enter a valid email";
    if (!/^[\d\s+\-()]{7,15}$/.test(form.phone)) return "Enter a valid phone number";
    if (!form.branch) return "Select your branch";
    if (!form.year) return "Select your year";
    return "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      setError(err);
      toast.error(err);
      return;
    }
    setError("");
    setLoading(true);
    try {
      const res = await api.post("/candidate/start", form);
      const { candidate_id, already_started, submission_status, candidate_code } = res.data;
      localStorage.setItem("bh_candidate_id", candidate_id);
      localStorage.setItem("bh_candidate_email", form.email);
      if (already_started) {
        toast.info(`Welcome back, ${candidate_code}. Resuming your session.`);
      } else {
        toast.success(`Timer started. ${candidate_code} — let's go.`);
      }
      if (submission_status === "submitted") {
        navigate(`/success/${candidate_id}`);
      } else {
        navigate(`/assignment/${candidate_id}`);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Unable to start. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] text-white" data-testid="start-form-page">
      <Header />
      <main className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 py-16 lg:py-24">
        <SectionLabel className="mb-6">Step 1 of 2 — Identity</SectionLabel>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mb-4">
          Tell us who you are.
        </h1>
        <p className="text-zinc-400 mb-12 max-w-xl">
          We only use this to pair your submission with your application. Your{" "}
          <span className="text-white font-semibold">4h 30m</span> timer starts the
          moment you click <span className="text-white font-semibold">Start Assignment</span>.
        </p>

        <form onSubmit={onSubmit} className="space-y-6" data-testid="start-form">
          <div>
            <label className="bh-label">Full name</label>
            <input
              data-testid="start-input-name"
              type="text"
              value={form.full_name}
              onChange={set("full_name")}
              placeholder="e.g. Ananya Sharma"
              className="bh-input"
              autoComplete="name"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label className="bh-label">Email</label>
              <input
                data-testid="start-input-email"
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="you@hbtu.ac.in"
                className="bh-input"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="bh-label">Phone</label>
              <input
                data-testid="start-input-phone"
                type="tel"
                value={form.phone}
                onChange={set("phone")}
                placeholder="+91 9XXXXXXXXX"
                className="bh-input"
                autoComplete="tel"
              />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label className="bh-label">Branch</label>
              <select
                data-testid="start-input-branch"
                value={form.branch}
                onChange={set("branch")}
                className="bh-input"
              >
                <option value="">Select branch</option>
                {BRANCHES.map((b) => (
                  <option key={b} value={b} className="bg-[#0e0e10]">
                    {b}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="bh-label">Year</label>
              <select
                data-testid="start-input-year"
                value={form.year}
                onChange={set("year")}
                className="bh-input"
              >
                <option value="">Select year</option>
                {YEARS.map((y) => (
                  <option key={y} value={y} className="bg-[#0e0e10]">
                    {y}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {error && (
            <div className="text-sm text-[#ff4950] border border-[var(--bh-accent-border)] bg-[var(--bh-accent-soft)] px-4 py-3 rounded-sm">
              {error}
            </div>
          )}

          <div className="pt-4 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <button
              data-testid="start-submit-btn"
              type="submit"
              disabled={loading}
              className="bh-btn-primary"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Start Assignment Now
              <ArrowRight className="w-4 h-4" />
            </button>
            <p className="text-xs text-zinc-400 max-w-xs">
              By clicking, you confirm the 4h 30m timer will begin immediately and{" "}
              <span className="text-[#ff4950]">cannot be reset</span>.
            </p>
          </div>
        </form>
      </main>
    </div>
  );
}
